import { useCallback, useEffect, useRef, useState } from 'react';

import { useCloseFloatingOnScroll } from './useCloseFloatingOnScroll';

import type { RefObject } from 'react';

interface UseOverflowTooltipResult {
  isOpen: boolean;
  contentRef: RefObject<HTMLSpanElement>;
  openHandler(): void;
  closeHandler(): void;
}

const OVERFLOW_TOLERANCE_PX = 1;

// Ensures at most one overflow tooltip is visible at a time: stores the close
// callback of whichever tooltip is currently open.
let closeActiveTooltip: (() => void) | null = null;

// Controlled lifecycle for an overflow-only tooltip.
//
// Overflow is measured on demand (on hover/focus) instead of on every render
// for every cell: measuring all instances on mount forces a synchronous layout
// read per cell and, with a ResizeObserver, keeps re-measuring on layout
// changes — the main source of layout thrash in long lists/tables. The tooltip
// opens only when the trigger is actually clipped and closes on leave, blur,
// scroll, or unmount, so the popup never lingers after the table moves or the
// virtualized row unmounts.
export const useOverflowTooltip = (
  disabled = false,
  titleKey?: string
): UseOverflowTooltipResult => {
  const contentRef = useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const closeHandler = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openHandler = useCallback(() => {
    if (disabled) return;

    const element = contentRef.current;

    if (!element) return;

    const hasVerticalOverflow =
      element.scrollHeight - element.clientHeight > OVERFLOW_TOLERANCE_PX;
    const hasHorizontalOverflow =
      element.scrollWidth - element.clientWidth > OVERFLOW_TOLERANCE_PX;

    if (hasVerticalOverflow || hasHorizontalOverflow) {
      // Close any other open tooltip before opening this one.
      if (closeActiveTooltip !== closeHandler) {
        closeActiveTooltip?.();
      }
      closeActiveTooltip = closeHandler;
      setIsOpen(true);
    }
  }, [disabled, closeHandler]);

  // Close when the displayed text changes while open (handles WebSocket row
  // updates that re-render the cell in place without unmounting this component).
  const prevTitleKeyRef = useRef(titleKey);
  useEffect(() => {
    if (
      isOpen &&
      titleKey !== undefined &&
      prevTitleKeyRef.current !== titleKey
    ) {
      closeHandler();
    }
    prevTitleKeyRef.current = titleKey;
  }, [isOpen, titleKey, closeHandler]);

  useCloseFloatingOnScroll(isOpen, closeHandler);

  // Release the singleton slot on unmount so a stale reference doesn't block
  // the next tooltip from registering (also avoids calling setState on an
  // unmounted component in development strict-mode double-invoke).
  useEffect(() => {
    return () => {
      if (closeActiveTooltip === closeHandler) {
        closeActiveTooltip = null;
      }
    };
  }, [closeHandler]);

  return { isOpen, contentRef, openHandler, closeHandler };
};
